import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { storage } from '../firebase/config';
import { ref, getDownloadURL } from 'firebase/storage';
import PlaylistSaveDialog from './PlaylistSaveDialog';
import './SwipeOverlay.css';

function SwipeOverlay({
  queue,
  currentIndex,
  onKeep,
  onReject,
  onClose,
  mode = 'listening', // 'preview' or 'listening'
  onSavePlaylist,
  onBlockPermanently,
  onNavigate, // Optional: callback for navigation (index) - used in navigation mode
  currentPlaylist, // Optional: current playlist info (name, createdBy, etc.)
  onModeChange, // Optional: callback to toggle between preview and listening mode
  user, // User object for playlist operations
  isLiveSave = false, // If true, hide Save button since tracks are saved automatically
  liveSavePlaylistInfo = null, // Playlist info when in live save mode (name, trackCount)
}) {
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [swipeStartY, setSwipeStartY] = useState(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const audioRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [previewDuration, setPreviewDuration] = useState(-1); // Default -1 means play full song, positive numbers are seconds
  const [previewTimeRemaining, setPreviewTimeRemaining] = useState(0);
  const previewStartTimeRef = useRef(null); // Track when preview started (audio time)
  const previewTimerRef = useRef(null); // Ref to track the preview timer timeout
  const previewIntervalRef = useRef(null); // Ref to track the preview timer interval
  const [isNavigationMode, setIsNavigationMode] = useState(false); // Toggle between Skip/Keep and Previous/Next
  const [navigationIndex, setNavigationIndex] = useState(null); // Local index for navigation mode

  // Fine filtering mode: track which side panel track is selected/playing
  const [selectedSidePanelTrack, setSelectedSidePanelTrack] = useState(null);
  const [selectedSidePanelSource, setSelectedSidePanelSource] = useState(null); // 'kept' or 'rejected'
  const [sidePanelAudioUrl, setSidePanelAudioUrl] = useState(null);
  const [isSidePanelAudioPlaying, setIsSidePanelAudioPlaying] = useState(false);
  const [loadingSidePanelAudio, setLoadingSidePanelAudio] = useState(false);
  const sidePanelAudioRef = useRef(null);

  // Device detection state
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [orientation, setOrientation] = useState(
    window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  );

  // View toggle state: 'card' (swipe card view) or 'lists' (side panels view)
  const [viewMode, setViewMode] = useState('card');

  // Use navigationIndex when in navigation mode, otherwise use currentIndex
  const effectiveIndex =
    mode === 'preview' && isNavigationMode && navigationIndex !== null
      ? navigationIndex
      : currentIndex;
  // Helper function to convert ALL CAPS to Title Case
  const toTitleCase = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Check if text contains any lowercase letters
    const hasLowercase = /[a-z]/.test(text);
    const hasUppercase = /[A-Z]/.test(text);
    
    // If there are uppercase letters but no lowercase letters, it's ALL CAPS
    if (hasUppercase && !hasLowercase) {
      // Convert to titlecase: first letter of each word uppercase, rest lowercase
      return text
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    
    return text;
  };

  const currentTrack = queue[effectiveIndex];
  const displayArtist = toTitleCase(currentTrack?.album?.artist || 'Unknown Artist');
  const displayAlbum = toTitleCase(currentTrack?.album?.album || 'Unknown Album');
  const displayTrack = toTitleCase(currentTrack?.DisplayTitle || currentTrack?.Title || 'Unknown Track');
  const displayPlaylistName = (currentPlaylist?.name || 'Unknown Playlist')
    .replace(/^Playlist:\s*/i, '')
    .replace(/^Master:?\s*/i, '')
    .replace(/\s*Master\s*/i, '');
  const displayPlaylistCreator = `${displayPlaylistName} Created by: ${currentPlaylist?.createdBy || 'Unknown Creator'}`;
  const keptTracks = queue.filter((t) => t.kept);
  const rejectedTracks = queue.filter((t) => t.rejected);
  const keptCount = keptTracks.length;
  const rejectedCount = rejectedTracks.length;
  
  // Calculate total track count for live save mode (existing + newly kept)
  const totalTrackCount = isLiveSave && liveSavePlaylistInfo
    ? (liveSavePlaylistInfo.trackCount || 0) + keptCount
    : keptCount;

  // Ref to store handleVoiceCommand to avoid closure issues
  const handleVoiceCommandRef = useRef(null);
  const isVoiceEnabledRef = useRef(isVoiceEnabled);
  const isAudioPlayingRef = useRef(isAudioPlaying); // Track audio playing state for voice recognition
  
  // Refs for Keep/Skip buttons to programmatically click them (desktop and mobile)
  const keepButtonRef = useRef(null);
  const rejectButtonRef = useRef(null);
  const keepButtonMobileRef = useRef(null);
  const rejectButtonMobileRef = useRef(null);
  
  // Debounce voice commands to prevent rapid-fire triggers
  const lastVoiceCommandTimeRef = useRef(0);
  const lastCommandWordRef = useRef(null); // Track last command word for duplicate detection
  const VOICE_COMMAND_DEBOUNCE_MS = 400; // Minimum 400ms between same commands to prevent duplicates

  // Update refs when state changes
  useEffect(() => {
    isVoiceEnabledRef.current = isVoiceEnabled;
  }, [isVoiceEnabled]);

  // Update ref when audio playing state changes (for logging purposes)
  useEffect(() => {
    isAudioPlayingRef.current = isAudioPlaying;
  }, [isAudioPlaying]);

  // Track window dimensions and orientation
  useEffect(() => {
    const updateDimensions = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
    };

    // Initial update
    updateDimensions();

    // Listen for resize and orientation changes
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, []);

  // Note: Preview mode is not supported in landscape orientation
  // We show a message instead of auto-switching to allow user to rotate device

  // Determine device type based on dimensions (matching CSS breakpoints)
  const getDeviceInfo = () => {
    const { width, height } = windowDimensions;
    const isLandscape = orientation === 'landscape';
    
    // First, check if it's actually a mobile device using user agent and touch capability
    const userAgent = navigator.userAgent || '';
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    // For mobile devices, check the smaller dimension (portrait width)
    // In landscape, width is the larger dimension (portrait height)
    const smallerDimension = isLandscape ? height : width;
    const largerDimension = isLandscape ? width : height;
    
    // Device detection logic
    let deviceType = 'Desktop';
    
    // If it's a mobile device (by UA or touch), prioritize mobile detection
    if (isMobileUA || hasTouch) {
      // Check user agent for specific device types
      const isPhoneUA = /iPhone|Android.*Mobile|Mobile.*Android/i.test(userAgent);
      const isTabletUA = /iPad|Tablet/i.test(userAgent);
      const isAndroid = /Android/i.test(userAgent);
      
      // S10 tablet detection: larger dimension >= 1200px indicates tablet
      // Check for S10 tablet first (larger dimension >= 1200px)
      if (largerDimension >= 1200) {
        // S10 tablet: portrait height >= 1200px
        deviceType = 'Tablet S10';
      }
      // For phones: smaller dimension typically <= 600px (even in landscape)
      // Phones in landscape usually have smaller dimension <= 600px
      else if (isPhoneUA || (isAndroid && !isTabletUA && smallerDimension <= 600) || (!isTabletUA && hasTouch && smallerDimension <= 600)) {
        // It's a phone
        if (smallerDimension <= 360) {
          deviceType = 'Mobile Phone S25';
        } else {
          deviceType = 'Mobile Phone';
        }
      }
      // Tablet detection: smaller dimension > 600px but < 1024px
      else if (isTabletUA || (smallerDimension > 600 && smallerDimension < 1024)) {
        deviceType = 'Tablet';
      }
      // Large tablet or small laptop-sized mobile device
      else if (smallerDimension >= 1024 && smallerDimension <= 1400) {
        deviceType = 'Tablet';
      }
      // Very large mobile device (unlikely but possible)
      else {
        deviceType = 'Mobile Device';
      }
    }
    // Desktop/laptop detection (no mobile UA, no touch)
    else {
      // Use larger dimension to detect S10 tablet (portrait height >= 1200px)
      const maxDimension = Math.max(width, height);
      const minDimension = Math.min(width, height);
      
      if (width <= 768) {
        deviceType = 'Small Screen';
      } else if (width > 768 && width < 1024) {
        deviceType = 'Tablet';
      } else if (width >= 1024 && width <= 1400) {
        // S10 tablet: larger dimension (portrait height) >= 1200px
        if (maxDimension >= 1200) {
          deviceType = 'Tablet S10';
        } else {
          deviceType = 'Laptop';
        }
      } else if (width > 1400) {
        deviceType = 'Desktop';
      } else {
        deviceType = 'Desktop';
      }
    }

    // Debug info for comparing emulator vs device
    const debugInfo = {
      windowInnerWidth: window.innerWidth,
      windowInnerHeight: window.innerHeight,
      windowOuterWidth: window.outerWidth,
      windowOuterHeight: window.outerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      devicePixelRatio: window.devicePixelRatio || 1,
      visualViewport: window.visualViewport ? {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      } : null,
    };

    return {
      device: deviceType,
      dimensions: `${width}x${height}`,
      orientation: isLandscape ? 'Landscape' : 'Portrait',
      debug: debugInfo, // Include debug info for comparison
    };
  };

  const deviceInfo = getDeviceInfo();

  // Initialize voice recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true; // Enable interim results for faster response
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        console.log('🎤 Voice recognition result received');
        const last = event.results.length - 1;
        const result = event.results[last];
        
        const transcript = result[0].transcript.toLowerCase().trim();
        const confidence = result[0].confidence || 0;
        
        // Require exact single-word match: transcript must be exactly "keep" or "skip"
        // This filters out lyrics that might contain these words
        const isExactMatch = (transcript === 'keep' || transcript === 'skip');
        
        if (!result.isFinal) {
          // For interim results, require exact match AND very high confidence (0.85)
          if (isExactMatch && confidence >= 0.85) {
            console.log('⚡ Processing exact match interim result:', transcript, 'confidence:', confidence.toFixed(2));
            // Process this interim result - continue below
          } else {
            // Silently skip - likely lyrics or noise
            return;
          }
        } else {
          // For final results, require exact match
          if (!isExactMatch) {
            // Silently skip - likely lyrics
            return;
          }
          console.log('✅ Processing exact match final result:', transcript, 'confidence:', confidence.toFixed(2));
        }
        
        // For final results, apply confidence threshold (interim results already filtered above)
        if (result.isFinal && confidence > 0 && confidence < 0.4) {
          console.log('❌ Final result confidence too low, ignoring:', transcript, 'confidence:', confidence.toFixed(2));
          return;
        }
        
        const now = Date.now();
        const timeSinceLastCommand = now - lastVoiceCommandTimeRef.current;
        
        // Debounce: only prevent if it's the SAME command word repeated too quickly
        // This allows switching between "keep" and "skip" quickly, but prevents duplicate triggers
        if (
          lastCommandWordRef.current === commandWord && 
          lastVoiceCommandTimeRef.current > 0 && 
          timeSinceLastCommand < VOICE_COMMAND_DEBOUNCE_MS
        ) {
          console.log('🔄 Duplicate command ignored (same command too soon):', commandWord, `(${Math.round(timeSinceLastCommand)}ms < ${VOICE_COMMAND_DEBOUNCE_MS}ms)`);
          return;
        }
        
        const commandWord = transcript; // At this point, transcript is exactly "keep" or "skip"
        console.log('🎤 Processing voice command:', commandWord, 'confidence:', confidence.toFixed(2), `(time since last: ${timeSinceLastCommand > 0 ? Math.round(timeSinceLastCommand) + 'ms' : 'first command'})`);
        lastVoiceCommandTimeRef.current = now;
        lastCommandWordRef.current = commandWord;
        
        if (handleVoiceCommandRef.current) {
          handleVoiceCommandRef.current(transcript);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Voice recognition error:', event.error);
        if (event.error === 'not-allowed') {
          setIsVoiceEnabled(false);
        }
      };

      recognitionInstance.onend = () => {
        // Restart recognition if voice is still enabled
        if (isVoiceEnabledRef.current) {
          try {
            recognitionInstance.start();
          } catch (e) {
            // Already started or not allowed - ignore
            console.log('Recognition restart failed:', e);
          }
        }
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  // Helper function to add visual feedback to button
  const flashButton = (buttonRef) => {
    if (buttonRef && buttonRef.current) {
      const button = buttonRef.current;
      button.classList.add('voice-activated');
      console.log('🎤 Voice command recognized! Button flashing:', button.className);
      
      // Remove the class after animation completes
      setTimeout(() => {
        button.classList.remove('voice-activated');
      }, 600);
    }
  };

  // Handle voice commands - ONLY listen for "keep" and "skip" words
  const handleVoiceCommand = (command) => {
    console.log('🎤 Voice command received:', command);
    
    // Normalize command to lowercase
    const normalizedCommand = command.toLowerCase();
    
    // Split into words for whole-word matching
    const words = normalizedCommand.split(/\s+/);
    
    // Helper function to check if a word exists as a whole word (not part of another word)
    const hasWord = (word) => {
      // Check if word is in the words array (exact match)
      if (words.includes(word)) return true;
      // Check with word boundaries regex
      const wordBoundaryRegex = new RegExp(`\\b${word}\\b`, 'i');
      return wordBoundaryRegex.test(normalizedCommand);
    };

    // ONLY match "keep" or "skip" - nothing else
    if (hasWord('keep')) {
      console.log('✅ Executing KEEP command - clicking Keep button');
      // Add visual feedback
      if (keepButtonRef.current) {
        flashButton(keepButtonRef);
        keepButtonRef.current.click();
      } else if (keepButtonMobileRef.current) {
        flashButton({ current: keepButtonMobileRef.current });
        keepButtonMobileRef.current.click();
      }
    } 
    else if (hasWord('skip')) {
      console.log('⏭️ Executing SKIP command - clicking Skip button');
      // Add visual feedback
      if (rejectButtonRef.current) {
        flashButton(rejectButtonRef);
        rejectButtonRef.current.click();
      } else if (rejectButtonMobileRef.current) {
        flashButton({ current: rejectButtonMobileRef.current });
        rejectButtonMobileRef.current.click();
      }
    } 
    else {
      console.log('⚠️ Voice command not recognized (only "keep" or "skip" accepted), ignoring:', command);
    }
  };

  // Update ref when handleVoiceCommand changes (no dependencies needed since we use refs for buttons)
  useEffect(() => {
    handleVoiceCommandRef.current = handleVoiceCommand;
  }, []);

  // Toggle voice recognition
  const toggleVoice = () => {
    if (!recognition) {
      alert('Voice recognition is not supported in your browser.');
      return;
    }

    if (isVoiceEnabled) {
      recognition.stop();
      setIsVoiceEnabled(false);
    } else {
      recognition.start();
      setIsVoiceEnabled(true);
    }
  };

  // Touch swipe handlers
  const handleTouchStart = (e) => {
    setSwipeStartX(e.touches[0].clientX);
    setSwipeStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!swipeStartX) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - swipeStartX;
    setSwipeOffset(diff);
  };

  const handleTouchEnd = () => {
    if (Math.abs(swipeOffset) > 100) {
      if (swipeOffset > 0) {
        handleKeep(); // Swipe right = keep
      } else {
        handleReject(); // Swipe left = reject
      }
    }
    setSwipeOffset(0);
    setSwipeStartX(0);
    setSwipeStartY(0);
  };

  // Mouse swipe handlers
  const [isMouseDown, setIsMouseDown] = useState(false);

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setSwipeStartX(e.clientX);
    setSwipeStartY(e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    const diff = e.clientX - swipeStartX;
    setSwipeOffset(diff);
  };

  const handleMouseUp = () => {
    if (Math.abs(swipeOffset) > 100) {
      if (swipeOffset > 0) {
        handleKeep();
      } else {
        handleReject();
      }
    }
    setSwipeOffset(0);
    setSwipeStartX(0);
    setIsMouseDown(false);
  };

  // Helper to find next unprocessed track index
  const findNextUnprocessedIndex = (startIndex) => {
    for (let i = startIndex + 1; i < queue.length; i++) {
      if (!queue[i].kept && !queue[i].rejected) {
        return i;
      }
    }
    return -1; // No next track found
  };

  // Helper to find previous unprocessed track index
  const findPreviousUnprocessedIndex = (startIndex) => {
    for (let i = startIndex - 1; i >= 0; i--) {
      if (!queue[i].kept && !queue[i].rejected) {
        return i;
      }
    }
    return -1; // No previous track found
  };

  // Initialize navigationIndex when entering navigation mode
  useEffect(() => {
    if (mode === 'preview' && isNavigationMode && navigationIndex === null) {
      setNavigationIndex(currentIndex);
    } else if (!isNavigationMode) {
      setNavigationIndex(null);
    }
  }, [mode, isNavigationMode, currentIndex, navigationIndex]);

  // Navigation handlers (Previous/Next)
  const handlePrevious = () => {
    const startIndex = effectiveIndex;
    const prevIndex = findPreviousUnprocessedIndex(startIndex);
    if (prevIndex !== -1) {
      // Stop audio before moving
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsAudioPlaying(false);
      previewStartTimeRef.current = null;
      // Navigate to previous track
      if (isNavigationMode) {
        setNavigationIndex(prevIndex);
      } else if (onNavigate) {
        onNavigate(prevIndex);
      }
      setSwipeOffset(0);
    }
  };

  const handleNext = () => {
    const startIndex = effectiveIndex;
    const nextIndex = findNextUnprocessedIndex(startIndex);
    if (nextIndex !== -1) {
      // Stop audio before moving
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsAudioPlaying(false);
      previewStartTimeRef.current = null;
      // Navigate to next track
      if (isNavigationMode) {
        setNavigationIndex(nextIndex);
      } else if (onNavigate) {
        onNavigate(nextIndex);
      }
      setSwipeOffset(0);
    }
  };

  // Action handlers
  const handleKeep = () => {
    // Clear any running preview timers to prevent conflicts
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }

    // In navigation mode, go to next track
    if (mode === 'preview' && isNavigationMode) {
      handleNext();
      return;
    }

    // Stop audio before moving to next
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsAudioPlaying(false);
    previewStartTimeRef.current = null; // Reset preview start time
    onKeep(effectiveIndex);
    setSwipeOffset(0);
  };

  const handleReject = () => {
    // In navigation mode, go to previous track
    if (mode === 'preview' && isNavigationMode) {
      handlePrevious();
      return;
    }

    // Stop audio before moving to next
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsAudioPlaying(false);
    previewStartTimeRef.current = null; // Reset preview start time
    onReject(effectiveIndex);
    setSwipeOffset(0);
  };

  const togglePlayPause = () => {
    if (!audioRef.current || !audioUrl) return;

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      // Stop side panel audio when main card starts playing
      if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
        sidePanelAudioRef.current.pause();
        sidePanelAudioRef.current.currentTime = 0;
        setIsSidePanelAudioPlaying(false);
      }

      audioRef.current.play();
      setIsAudioPlaying(true);
    }
  };

  const handleRewind = () => {
    if (!audioRef.current) return;
    const newTime = Math.max(0, audioRef.current.currentTime - 10);
    audioRef.current.currentTime = newTime;
  };

  const handleFastForward = () => {
    if (!audioRef.current) return;
    const duration = audioRef.current.duration || 0;
    const newTime = Math.min(duration, audioRef.current.currentTime + 10);
    audioRef.current.currentTime = newTime;
  };

  const handleBlock = () => {
    if (
      window.confirm(
        `Block "${currentTrack.album.artist}" permanently? This artist will never appear in your library.`
      )
    ) {
      onBlockPermanently(currentTrack);
      onReject(effectiveIndex);
    }
  };

  // Helper function to get a unique key for a track
  const getTrackKey = (track) => {
    return `${track.Title || ''}_${track.album?.artist || ''}_${
      track.album?.album || ''
    }`;
  };

  // Fine filtering mode: Handle clicking a track in the side panels
  const handleSidePanelTrackClick = async (track, source) => {
    // Pause the main center card audio when selecting a side panel track
    if (audioRef.current && isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    }

    const trackKey = getTrackKey(track);
    const selectedKey = selectedSidePanelTrack
      ? getTrackKey(selectedSidePanelTrack)
      : null;

    // If clicking the same track, toggle pause/play
    if (trackKey === selectedKey && selectedSidePanelSource === source) {
      if (sidePanelAudioRef.current) {
        if (isSidePanelAudioPlaying) {
          sidePanelAudioRef.current.pause();
          setIsSidePanelAudioPlaying(false);
        } else {
          try {
            await sidePanelAudioRef.current.play();
            setIsSidePanelAudioPlaying(true);
          } catch (err) {
            console.error('Error playing side panel audio:', err);
          }
        }
      }
    } else {
      // Stop the previous side panel track before selecting a new one
      if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
        sidePanelAudioRef.current.pause();
        sidePanelAudioRef.current.currentTime = 0;
        setIsSidePanelAudioPlaying(false);
      }

      // Select new track - audio will load via useEffect, then auto-play
      setSelectedSidePanelTrack(track);
      setSelectedSidePanelSource(source);
    }
  };

  // Auto-play side panel audio when loaded
  useEffect(() => {
    if (
      sidePanelAudioUrl &&
      sidePanelAudioRef.current &&
      selectedSidePanelTrack
    ) {
      const audio = sidePanelAudioRef.current;

      // Stop main card audio before playing side panel audio
      if (audioRef.current && isAudioPlaying) {
        audioRef.current.pause();
        setIsAudioPlaying(false);
        // Pause the preview timer by keeping the current state
      }

      // Wait for audio to be ready, then play
      const tryPlay = () => {
        if (audio.readyState >= 2) {
          // HAVE_CURRENT_DATA or higher
          audio.currentTime = 0;
          const playPromise = audio.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsSidePanelAudioPlaying(true);
              })
              .catch((err) => {
                console.error('Error playing side panel audio:', err);
                // User interaction is required - track is selected, they can click to play
              });
          }
        } else {
          // Wait for audio to load more data
          audio.addEventListener('canplay', tryPlay, { once: true });
        }
      };

      tryPlay();

      // Cleanup listener if component unmounts or dependencies change
      return () => {
        audio.removeEventListener('canplay', tryPlay);
      };
    }
  }, [sidePanelAudioUrl, selectedSidePanelTrack]);

  // Fine filtering mode: Move track to opposite list
  const handleMoveTrackToOppositeList = (track, currentSource) => {
    // Find the track's index in the original queue
    const trackIndex = queue.findIndex(
      (t) =>
        t.Title === track.Title &&
        t.album?.artist === track.album?.artist &&
        t.album?.album === track.album?.album
    );

    if (trackIndex !== -1) {
      if (currentSource === 'kept') {
        // Move from kept to rejected
        onReject(trackIndex);
      } else {
        // Move from rejected to kept
        onKeep(trackIndex);
      }

      // Clear selection after moving
      setSelectedSidePanelTrack(null);
      setSelectedSidePanelSource(null);
    }
  };

  const handleSave = () => {
    console.log('💾 handleSave called - showing save dialog');
    setShowSaveDialog(true);
    console.log('💾 showSaveDialog state set to true');
  };

  const handleSaveWithName = (playlistData) => {
    console.log(
      '💾 handleSaveWithName called with playlistData:',
      playlistData
    );
    
    // Stop the media player
    if (audioRef.current && isAudioPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsAudioPlaying(false);
      console.log('⏹️ Media player stopped');
    }
    
    // Also stop side panel audio if playing
    if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
      sidePanelAudioRef.current.pause();
      sidePanelAudioRef.current.currentTime = 0;
      setIsSidePanelAudioPlaying(false);
      console.log('⏹️ Side panel audio stopped');
    }
    
    const keptTracks = queue.filter((t) => t.kept);
    console.log('💾 Kept tracks count:', keptTracks.length);
    console.log('💾 Calling onSavePlaylist callback');
    if (onSavePlaylist) {
      onSavePlaylist(keptTracks, playlistData);
    } else {
      console.error('❌ onSavePlaylist callback is not defined!');
    }
    setShowSaveDialog(false);
    console.log('💾 Dialog closed');
  };

  const handleClose = () => {
    if (recognition && isVoiceEnabled) {
      recognition.stop();
    }
    onClose();
  };

  // Helper function to load audio for a track
  const loadAudioForTrack = async (track, setUrl, setLoading) => {
    if (!track?.Title) {
      setUrl(null);
      return;
    }

    const type = track.album.type || 'Albums';
    // For Albums, require filePath. For Singles, we construct from fields so filePath is optional
    if (type !== 'Singles' && !track?.album?.filePath) {
      setUrl(null);
      return;
    }

    setLoading(true);
    try {
      let audioPath;
      const filename = track.Title;
      const language = track.album.language || 'Unknown';

      // For Singles, always construct the path correctly (filePath may be wrong)
      if (type === 'Singles') {
        // For Singles path structure (Album comes BEFORE Artist):
        // English: Singles/English/Various/Variety/Album/Mix/Song
        // Spanish (and others): Singles/Language/Various/Variety/Album/Artist/Song
        const genre = track.album.genre || 'Various';
        const subgenre = track.album.subgenre || 'Variety';
        const album = track.album.album || 'Unknown';
        // For English, use "Mix", for other languages use the artist field
        const artistOrMix =
          language === 'English'
            ? 'Mix'
            : track.album.artist ||
              track.album.artistName ||
              'Mix';
        // For Singles: Album comes BEFORE Artist in the path
        // Note: album.album field contains Artist, album.artist field contains Album
        audioPath = `Singles/${language}/${genre}/${subgenre}/${artistOrMix}/${album}/${filename}`;
        console.log('Loading Singles audio from:', audioPath);
      } else {
        // For Albums: use filePath from DB
        let filePath = track.album.filePath;
        if (filePath.startsWith('/')) {
          filePath = filePath.substring(1);
        }

        // Prepend the type (Albums) if not already in the path
        if (!filePath.startsWith(type + '/')) {
          filePath = `${type}/${filePath}`;
        }

        if (!filePath.endsWith('/')) {
          filePath += '/';
        }

        audioPath = `${filePath}${filename}`;
        console.log('Loading Albums audio from:', audioPath);
      }

      const audioStorageRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioStorageRef);
      setUrl(url);
    } catch (error) {
      console.error('Error loading audio:', error);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // Load audio for current track
  useEffect(() => {
    // Reset preview start time when track changes
    previewStartTimeRef.current = null;
    loadAudioForTrack(currentTrack, setAudioUrl, setLoadingAudio);
  }, [currentTrack]);

  // Load audio for selected side panel track
  useEffect(() => {
    if (selectedSidePanelTrack) {
      loadAudioForTrack(
        selectedSidePanelTrack,
        setSidePanelAudioUrl,
        setLoadingSidePanelAudio
      );
    } else {
      setSidePanelAudioUrl(null);
      if (sidePanelAudioRef.current) {
        sidePanelAudioRef.current.pause();
        sidePanelAudioRef.current.currentTime = 0;
      }
      setIsSidePanelAudioPlaying(false);
    }
  }, [selectedSidePanelTrack]);

  // Auto-play audio when loaded (only in preview/listening mode after user interaction)
  useEffect(() => {
    if (audioUrl && audioRef.current && mode === 'preview') {
      // Stop any side panel audio before starting main card audio
      if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
        sidePanelAudioRef.current.pause();
        sidePanelAudioRef.current.currentTime = 0;
        setIsSidePanelAudioPlaying(false);
        setSelectedSidePanelTrack(null);
        setSelectedSidePanelSource(null);
      }

      // Only auto-play in preview mode (user has already interacted to open overlay)
      audioRef.current
        .play()
        .then(() => {
          setIsAudioPlaying(true);
        })
        .catch((err) => {
          // Handle autoplay restrictions gracefully
          if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
            console.log('⚠️ Autoplay blocked in preview - user can click play');
            setIsAudioPlaying(false);
          } else {
            console.error('Error playing audio:', err);
          }
        });
    }
  }, [audioUrl, mode]);

  // Preview mode: auto-advance after selected duration (unless playing full song)
  // Timer only runs when audio is actually playing
  useEffect(() => {
    let countdownInterval = null;
    let timer = null;

    if (
      mode === 'preview' &&
      currentTrack &&
      audioUrl &&
      previewDuration > 0 &&
      isAudioPlaying
    ) {
      // Track when preview starts (when audio starts playing)
      const updateTimer = () => {
        if (!audioRef.current || !isAudioPlaying) {
          // Audio was paused, stop updating timer
          if (countdownInterval) {
            clearInterval(countdownInterval);
            countdownInterval = null;
          }
          return;
        }

        const currentAudioTime = audioRef.current.currentTime;

        // Initialize preview start time if not set or if audio was reset (went backwards)
        if (
          previewStartTimeRef.current === null ||
          currentAudioTime < previewStartTimeRef.current
        ) {
          previewStartTimeRef.current = currentAudioTime;
        }

        // Calculate remaining time based on current audio position
        const elapsed = currentAudioTime - previewStartTimeRef.current;
        const remaining = Math.max(0, previewDuration - elapsed);

        setPreviewTimeRemaining(Math.ceil(remaining));

        // If time is up, advance to next track
        if (remaining <= 0) {
          if (countdownInterval) {
            clearInterval(countdownInterval);
            countdownInterval = null;
          }
          if (timer) {
            clearTimeout(timer);
            timer = null;
          }
          handleKeep();
          return;
        }
      };

      // Update timer immediately and then frequently for smooth updates
      updateTimer();
      countdownInterval = setInterval(updateTimer, 100);
      previewIntervalRef.current = countdownInterval; // Store in ref for cleanup

      // Calculate initial remaining time for the timeout
      const currentAudioTime = audioRef.current?.currentTime || 0;
      if (
        previewStartTimeRef.current === null ||
        currentAudioTime < previewStartTimeRef.current
      ) {
        previewStartTimeRef.current = currentAudioTime;
      }
      const elapsed = currentAudioTime - previewStartTimeRef.current;
      const remaining = Math.max(0, previewDuration - elapsed);

      timer = setTimeout(() => {
        handleKeep(); // Auto-keep in preview mode (backup in case interval doesn't catch it)
        if (countdownInterval) {
          clearInterval(countdownInterval);
        }
        setPreviewTimeRemaining(0);
        previewStartTimeRef.current = null;
        previewTimerRef.current = null;
        previewIntervalRef.current = null;
      }, remaining * 1000);
      previewTimerRef.current = timer; // Store in ref for cleanup

      return () => {
        if (timer) {
          clearTimeout(timer);
          previewTimerRef.current = null;
        }
        if (countdownInterval) {
          clearInterval(countdownInterval);
          previewIntervalRef.current = null;
        }
      };
    } else if (
      mode === 'preview' &&
      !isAudioPlaying &&
      previewTimeRemaining > 0
    ) {
      // Timer is paused - don't update, just keep the current remaining time
      // This allows the timer to resume from where it was paused when audio plays again
      // previewStartTimeRef.current is preserved so elapsed time calculation continues correctly
    } else {
      // Reset timer display when not in preview mode or playing full song
      if (mode !== 'preview' || previewDuration <= 0) {
        setPreviewTimeRemaining(0);
        previewStartTimeRef.current = null;
      }
    }
    // If previewDuration is -1 (play full song), don't auto-advance
    // User will manually advance after song ends or via swipe/buttons
  }, [currentIndex, mode, audioUrl, previewDuration, isAudioPlaying]);

  // When all tracks are processed, keep the interface visible for fine filtering
  const allTracksProcessed = !currentTrack;

  const swipeStyle = {
    transform: `translateX(${swipeOffset}px) rotate(${swipeOffset / 20}deg)`,
    opacity: Math.max(0.5, 1 - Math.abs(swipeOffset) / 300),
  };

  return (
    <div className="swipe-overlay" data-mode={mode}>
      {/* Landscape orientation warning for Preview mode - PHONES ONLY (not tablets) */}
      {mode === 'preview' && orientation === 'landscape' && windowDimensions.width <= 480 && (
        <div className="preview-landscape-warning">
          <div className="preview-landscape-message">
            <div className="warning-icon">📱</div>
            <div className="warning-text">
              <strong>Preview mode requires portrait orientation</strong>
              <p>Please rotate your device to portrait mode to continue</p>
              <div className="rotation-hint">↻ Rotate device</div>
            </div>
          </div>
        </div>
      )}
      {/* Header - All controls in one row */}
      <div className="swipe-header">
        <div className="swipe-mode-indicator">
          {isLiveSave && liveSavePlaylistInfo ? (
            <>
              <div style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                Appending to: {liveSavePlaylistInfo.name}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '400', opacity: 0.9 }}>
                {totalTrackCount} track{totalTrackCount !== 1 ? 's' : ''} total ({liveSavePlaylistInfo.trackCount || 0} existing + {keptCount} new)
              </div>
            </>
          ) : allTracksProcessed ? (
            '✓ FILTERING COMPLETE'
          ) : mode === 'preview' ? (
            '⚡ PREVIEW'
          ) : (
            '🎵 LISTENING'
          )}
        </div>

        {/* Action buttons in header */}
        <div className="header-actions">
          {!allTracksProcessed && (
            <button
              className="header-btn header-btn-block"
              onClick={handleBlock}
            >
              🚫
            </button>
          )}
          {!isLiveSave && (
            <button
              className="header-btn header-btn-save"
              onClick={(e) => {
                console.log('💾 Save button clicked in header');
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
            >
              💾 {queue.filter((t) => t.kept).length}
            </button>
          )}
          <button
            className={`header-btn header-btn-voice ${
              isVoiceEnabled ? 'active' : ''
            }`}
            onClick={toggleVoice}
          >
            🎤
          </button>
        </div>

        <button className="swipe-close-btn" onClick={handleClose}>
          ✕
        </button>
      </div>

      {/* Progress */}
      <div className="swipe-progress">
        <div className="swipe-progress-bar">
          <div
            className="swipe-progress-fill"
            style={{
              width: `${
                allTracksProcessed
                  ? 100
                  : ((effectiveIndex + 1) / queue.length) * 100
              }%`,
            }}
          />
        </div>
        <div className="swipe-stats">
          <span className="stat-rejected">✕ {rejectedCount}</span>
          <span className="stat-current">
            {allTracksProcessed
              ? `Complete: ${queue.length} / ${queue.length}`
              : `${effectiveIndex + 1} / ${queue.length}`}
          </span>
          <span className="stat-kept">✓ {keptCount}</span>
        </div>
      </div>

      {/* Preview Duration Selector - Below track counter */}
      {(mode === 'preview' || onModeChange) && (
        <div className="preview-duration-selector-mobile">
          {mode === 'preview' && (
            <>
              {/* Hide 3s button - removed */}
              <button
                className={`duration-btn ${previewDuration === 5 ? 'active' : ''}`}
                onClick={() => setPreviewDuration(5)}
              >
                5s
              </button>
              <button
                className={`duration-btn ${previewDuration === 20 ? 'active' : ''}`}
                onClick={() => setPreviewDuration(20)}
              >
                20s
              </button>
              <button
                className={`duration-btn ${previewDuration === -1 ? 'active' : ''}`}
                onClick={() => setPreviewDuration(-1)}
                title="Play entire song"
              >
                ▶ Full
              </button>
              {/* Navigation Mode Toggle */}
              <button
                className={`duration-btn ${isNavigationMode ? 'active' : ''}`}
                onClick={() => setIsNavigationMode(!isNavigationMode)}
                title={
                  isNavigationMode
                    ? 'Switch to Skip/Keep mode'
                    : 'Switch to Previous/Next navigation mode'
                }
                style={{ fontWeight: 'bold' }}
              >
                {isNavigationMode ? '👎❤️' : '⏮⏭'}
              </button>
            </>
          )}
          {/* Mode Toggle Button - Show Lists view or toggle to Preview mode */}
          {onModeChange && (
            <button
              className={`duration-btn ${mode === 'preview' ? 'active' : ''} ${viewMode === 'lists' ? 'lists-active' : ''} ${orientation === 'landscape' ? 'disabled' : ''}`}
              onClick={() => {
                if (orientation === 'landscape') {
                  return; // Disabled in landscape
                }
                if (mode === 'preview') {
                  // In preview mode, toggle between card view and lists view
                  setViewMode(prev => prev === 'card' ? 'lists' : 'card');
                } else {
                  // In listening mode, switch to preview mode
                  onModeChange('preview');
                }
              }}
              title={
                orientation === 'landscape'
                  ? 'Preview mode is only available in portrait orientation'
                  : mode === 'preview'
                  ? (viewMode === 'lists' ? 'Show Swipe Card' : 'Show Lists')
                  : 'Switch to Preview Mode'
              }
              disabled={orientation === 'landscape'}
              style={{ fontWeight: 'bold', opacity: orientation === 'landscape' ? 0.5 : 1 }}
            >
              {mode === 'preview' ? (viewMode === 'lists' ? 'Card' : 'Lists') : '⚡ Preview'}
            </button>
          )}
        </div>
      )}

      {/* Swipe Direction Indicators - Show when actively swiping */}
      {swipeOffset < -30 && (
        <div
          className="swipe-direction-indicator swipe-direction-left"
          style={{ opacity: Math.min(1, Math.abs(swipeOffset) / 100) }}
        >
          <div className="direction-icon">
            {mode === 'preview' && isNavigationMode ? '⏮' : '👎'}
          </div>
          <div className="direction-text">
            {mode === 'preview' && isNavigationMode ? 'PREV' : 'SKIP'}
          </div>
        </div>
      )}

      {swipeOffset > 30 && (
        <div
          className="swipe-direction-indicator swipe-direction-right"
          style={{ opacity: Math.min(1, swipeOffset / 100) }}
        >
          <div className="direction-icon">
            {mode === 'preview' && isNavigationMode ? '⏭' : '❤️'}
          </div>
          <div className="direction-text">
            {mode === 'preview' && isNavigationMode ? 'NEXT' : 'KEEP'}
          </div>
        </div>
      )}

      {/* Flex-grid container below progress bar */}
      <div className="flex-grid-container">
        {/* Display Artist Element - Hidden (duplicate of swipe-card display) */}
        <div className="display-artist-element" style={{ display: 'none' }}>
          {displayArtist}
        </div>
        
        {/* Display Album Element - Hidden (duplicate of swipe-card display) */}
        <div className="display-album-element" style={{ display: 'none' }}>
          {displayAlbum}
        </div>
        
        {/* Display Track Element - Hidden (duplicate of swipe-card display) */}
        <div className="display-track-element" style={{ display: 'none' }}>
          {displayTrack}
        </div>
        
        {/* Display Playlist Creator Element (with playlist name) - Hidden (duplicate of swipe-card display) */}
        <div className="display-playlist-creator-element" style={{ display: 'none' }}>
          {displayPlaylistCreator}
        </div>
        
        {/* Media Buttons - Only rewind, play/pause, fast forward (Keep/Skip moved to Row 2) */}
        {mode === 'preview' && audioUrl && (
          <div className="swipe-preview-controls">
            <button
              className="swipe-preview-control-btn swipe-rewind-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRewind();
              }}
              title="Rewind 10 seconds"
            >
              ⏪
            </button>
            <button
              className="swipe-preview-control-btn swipe-play-pause-btn"
              onClick={(e) => {
                e.stopPropagation();
                togglePlayPause();
              }}
              title={isAudioPlaying ? 'Pause' : 'Play'}
            >
              {isAudioPlaying ? '⏸' : '▶'}
            </button>
            <button
              className="swipe-preview-control-btn swipe-ff-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleFastForward();
              }}
              title="Fast forward 10 seconds"
            >
              ⏩
            </button>
          </div>
        )}
        
        {/* Playlist Name Display - Above swipe card */}
        {isLiveSave && liveSavePlaylistInfo && (
          <div className="playlist-title-above-card" style={{
            textAlign: 'center',
            marginBottom: '0',
            marginLeft: '1rem',
            marginRight: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'rgba(30, 144, 255, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(30, 144, 255, 0.3)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#1E90FF'
            }}>
              {liveSavePlaylistInfo.name}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#b3b3b3',
              opacity: 0.9
            }}>
              {totalTrackCount} track{totalTrackCount !== 1 ? 's' : ''} total ({liveSavePlaylistInfo.trackCount || 0} existing + {keptCount} new)
            </div>
          </div>
        )}

        {/* Card with Side Buttons - Hide when all tracks processed */}
        {!allTracksProcessed ? (
          <div className={`swipe-card-wrapper ${viewMode === 'lists' ? 'lists-view' : 'card-view'}`}>
          <div className="swipe-card-inner-wrapper">
          {/* Side Panels - Inside blue container */}
          {/* Always show in S10 tablet landscape, or in lists view mode */}
          {!allTracksProcessed && (viewMode === 'lists' || (deviceInfo.device === 'Tablet S10' && orientation === 'landscape')) && (
            <>
              {/* LEFT Side Panel - Skipped Tracks - First in row (direct child of swipe-card-wrapper) */}
              <div className="swipe-side-panel swipe-side-panel-left">
                <div className="side-panel-header">
                  <span className="side-panel-icon">👎</span>
                  <span className="side-panel-title">Skipped ({rejectedCount})</span>
                </div>
                <div className="side-panel-list">
                  {rejectedTracks.length === 0 ? (
                    <div className="side-panel-empty">
                      <span>← Swipe left to skip</span>
                    </div>
                  ) : (
                    rejectedTracks.map((track, idx) => {
                      const trackKey = getTrackKey(track);
                      const selectedKey = selectedSidePanelTrack
                        ? getTrackKey(selectedSidePanelTrack)
                        : null;
                      const isSelected =
                        trackKey === selectedKey &&
                        selectedSidePanelSource === 'rejected';
                      return (
                        <div
                          key={idx}
                          className={`side-panel-track rejected ${
                            isSelected ? 'playing' : ''
                          }`}
                          onClick={() => handleSidePanelTrackClick(track, 'rejected')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span className="track-title">
                                {track.DisplayTitle || track.Title}
                              </span>
                              <span className="track-artist">
                                {track.album?.artist}
                              </span>
                            </div>
                            {isSelected && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveTrackToOppositeList(track, 'rejected');
                                }}
                                className="side-panel-move-btn"
                                title="Move to Kept"
                                style={{
                                  background: '#4CAF50',
                                  border: 'none',
                                  color: '#fff',
                                  borderRadius: '4px',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  flexShrink: 0,
                                }}
                              >
                                ✓ Keep
                              </button>
                            )}
                          </div>
                          {isSelected && isSidePanelAudioPlaying && (
                            <div
                              style={{
                                marginTop: '0.25rem',
                                fontSize: '0.6rem',
                                color: '#1E90FF',
                              }}
                            >
                              ▶ Playing
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
          {/* Large SKIP/PREV button on left (desktop) */}
          <button
            ref={rejectButtonRef}
            className="side-action-btn side-btn-reject desktop-only"
            onClick={handleReject}
          >
            <span className="side-btn-icon">
              {mode === 'preview' && isNavigationMode ? '⏮' : '👎'}
            </span>
            <span className="side-btn-label">
              {mode === 'preview' && isNavigationMode ? 'PREV' : 'SKIP'}
            </span>
          </button>

          <div
            className={`swipe-card-container ${viewMode === 'lists' ? 'hidden' : ''}`}
            onTouchStart={(e) => {
              // Stop side panel audio when interacting with main card
              if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
                sidePanelAudioRef.current.pause();
                sidePanelAudioRef.current.currentTime = 0;
                setIsSidePanelAudioPlaying(false);
                setSelectedSidePanelTrack(null);
                setSelectedSidePanelSource(null);
              }
              handleTouchStart(e);
            }}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={(e) => {
              // Stop side panel audio when interacting with main card
              if (sidePanelAudioRef.current && isSidePanelAudioPlaying) {
                sidePanelAudioRef.current.pause();
                sidePanelAudioRef.current.currentTime = 0;
                setIsSidePanelAudioPlaying(false);
                setSelectedSidePanelTrack(null);
                setSelectedSidePanelSource(null);
              }
              handleMouseDown(e);
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => isMouseDown && handleMouseUp()}
          >
            <div className="swipe-card" style={swipeStyle}>
              {/* Card corner indicators */}
              {swipeOffset > 50 && (
                <div className="swipe-indicator swipe-keep">
                  {mode === 'preview' && isNavigationMode
                    ? '⏭ NEXT'
                    : '❤️ KEEP'}
                </div>
              )}
              {swipeOffset < -50 && (
                <div className="swipe-indicator swipe-reject">
                  {mode === 'preview' && isNavigationMode
                    ? '⏮ PREV'
                    : '👎 SKIP'}
                </div>
              )}

              {/* Track info */}
              <h2 className="swipe-track-title">
                {displayTrack}
              </h2>
              <p className="swipe-track-artist">{displayArtist}</p>
              <p className="swipe-track-album">
                {(() => {
                  const albumParts = displayAlbum.split(' - ');
                  return (
                    <span className="album-content">
                      <span className="album-main">{albumParts[0]}</span>
                      {albumParts.length > 1 && (
                        <span className="album-subtitle">{albumParts[1]}</span>
                      )}
                    </span>
                  );
                })()}
              </p>
              {currentPlaylist && (
                <div className="swipe-playlist-info">
                  <p className="swipe-playlist-name">
                    📋 {currentPlaylist.name}
                  </p>
                  <p className="swipe-playlist-creator">
                    Created by: {currentPlaylist.createdBy}
                  </p>
                </div>
              )}
              {mode === 'preview' && previewDuration > 0 && (
                <p className="swipe-preview-timer">
                  {`${previewTimeRemaining || 0}s${
                    !isAudioPlaying && previewTimeRemaining > 0
                      ? ' (Paused)'
                      : ''
                  }`}
                </p>
              )}
              {/* Preview mode controls: Rewind, Play/Pause, and Fast Forward - Always show in preview mode */}
              {mode === 'preview' && (
                <div className="swipe-preview-controls" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
                  <button
                    className="swipe-preview-control-btn swipe-rewind-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (audioUrl) handleRewind();
                    }}
                    disabled={!audioUrl}
                    title={audioUrl ? "Rewind 10 seconds" : "Loading audio..."}
                  >
                    ⏪
                  </button>
                  <button
                    className="swipe-preview-control-btn swipe-play-pause-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (audioUrl) togglePlayPause();
                    }}
                    disabled={!audioUrl}
                    title={audioUrl ? (isAudioPlaying ? 'Pause' : 'Play') : "Loading audio..."}
                  >
                    {loadingAudio ? '⏳' : (isAudioPlaying ? '⏸' : '▶')}
                  </button>
                  <button
                    className="swipe-preview-control-btn swipe-ff-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (audioUrl) handleFastForward();
                    }}
                    disabled={!audioUrl}
                    title={audioUrl ? "Fast forward 10 seconds" : "Loading audio..."}
                  >
                    ⏩
                  </button>
                </div>
              )}
              {loadingAudio && (
                <p className="swipe-loading-audio">Loading audio...</p>
              )}
            </div>
          </div>

          {/* Hidden audio element for main track */}
          {audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => {
                if (mode === 'listening') {
                  handleKeep(); // Auto-advance when song ends in listening mode
                } else if (mode === 'preview' && previewDuration === -1) {
                  // If playing full song in preview mode, auto-advance when song ends
                  handleKeep();
                }
              }}
              onError={(e) => {
                console.error('Audio playback error:', e);
              }}
            />
          )}

          {/* Hidden audio element for side panel track (fine filtering mode) */}
          {sidePanelAudioUrl && (
            <audio
              ref={sidePanelAudioRef}
              src={sidePanelAudioUrl}
              onEnded={() => {
                setIsSidePanelAudioPlaying(false);
              }}
              onError={(e) => {
                console.error('Side panel audio playback error:', e);
                setIsSidePanelAudioPlaying(false);
              }}
            />
          )}

          {/* Large KEEP/NEXT button on right (desktop) */}
          <button
            ref={keepButtonRef}
            className="side-action-btn side-btn-keep desktop-only"
            onClick={handleKeep}
          >
            <span className="side-btn-icon">
              {mode === 'preview' && isNavigationMode ? '⏭' : '❤️'}
            </span>
            <span className="side-btn-label">
              {mode === 'preview' && isNavigationMode ? 'NEXT' : 'KEEP'}
            </span>
          </button>

          {/* RIGHT Side Panel - Kept Tracks - Last in row (direct child of swipe-card-wrapper) */}
          {!allTracksProcessed && (viewMode === 'lists' || (deviceInfo.device === 'Tablet S10' && orientation === 'landscape')) && (
            <div className="swipe-side-panel swipe-side-panel-right">
              <div className="side-panel-header">
                <span className="side-panel-icon">❤️</span>
                <span className="side-panel-title">Kept ({keptCount})</span>
              </div>
              <div className="side-panel-list">
                {keptTracks.length === 0 ? (
                  <div className="side-panel-empty">
                    <span>Swipe right to keep →</span>
                  </div>
                ) : (
                  keptTracks.map((track, idx) => {
                    const trackKey = getTrackKey(track);
                    const selectedKey = selectedSidePanelTrack
                      ? getTrackKey(selectedSidePanelTrack)
                      : null;
                    const isSelected =
                      trackKey === selectedKey && selectedSidePanelSource === 'kept';
                    return (
                      <div
                        key={idx}
                        className={`side-panel-track kept ${
                          isSelected ? 'playing' : ''
                        }`}
                        onClick={() => handleSidePanelTrackClick(track, 'kept')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span className="track-title">
                              {track.DisplayTitle || track.Title}
                            </span>
                            <span className="track-artist">
                              {track.album?.artist}
                            </span>
                          </div>
                          {isSelected && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveTrackToOppositeList(track, 'kept');
                              }}
                              className="side-panel-move-btn"
                              title="Move to Skipped"
                              style={{
                                background: '#f44336',
                                border: 'none',
                                color: '#fff',
                                borderRadius: '4px',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                                fontWeight: '600',
                                flexShrink: 0,
                              }}
                            >
                              ✕ Skip
                            </button>
                          )}
                        </div>
                        {isSelected && isSidePanelAudioPlaying && (
                          <div
                            style={{
                              marginTop: '0.25rem',
                              fontSize: '0.6rem',
                              color: '#1E90FF',
                            }}
                          >
                            ▶ Playing
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Mobile action buttons - Square, below card */}
          <div className={`mobile-action-buttons ${viewMode === 'lists' ? 'hidden' : ''}`} style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
            <button
              ref={rejectButtonMobileRef}
              className="mobile-action-btn mobile-btn-reject"
              onClick={handleReject}
            >
              <span className="mobile-btn-icon">
                {mode === 'preview' && isNavigationMode ? '⏮' : '👎'}
              </span>
              <span className="mobile-btn-label">
                {mode === 'preview' && isNavigationMode ? 'PREV' : 'SKIP'}
              </span>
            </button>
            <button
              ref={keepButtonMobileRef}
              className="mobile-action-btn mobile-btn-keep"
              onClick={handleKeep}
            >
              <span className="mobile-btn-icon">
                {mode === 'preview' && isNavigationMode ? '⏭' : '❤️'}
              </span>
              <span className="mobile-btn-label">
                {mode === 'preview' && isNavigationMode ? 'NEXT' : 'KEEP'}
              </span>
            </button>
          </div>
          </div>
        </div>
        ) : null}
      </div>

      {/* Completion Message - Show when all tracks processed */}
      {allTracksProcessed && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '2rem',
            borderRadius: '12px',
            textAlign: 'center',
            zIndex: 10,
            border: '2px solid rgba(76, 175, 80, 0.5)',
          }}
        >
          <h2
            style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.5rem' }}
          >
            ✓ Filtering Complete!
          </h2>
          <p style={{ color: '#b3b3b3', marginBottom: '0.5rem' }}>
            Kept: {keptCount} songs
          </p>
          <p style={{ color: '#b3b3b3', marginBottom: '1.5rem' }}>
            Rejected: {rejectedCount} songs
          </p>
          <p
            style={{ color: '#fff', fontSize: '0.9rem', marginBottom: '1rem' }}
          >
            You can continue to fine-tune your playlist using the tracks in the
            side panels.
          </p>
          <p style={{ color: '#b3b3b3', fontSize: '0.85rem' }}>
            Click "💾 Save" in the header when you're ready to save your
            playlist.
          </p>
        </div>
      )}

      {/* Voice Commands Hint */}
      {isVoiceEnabled && (
        <div className="voice-commands-hint">
          Say: "keep", "skip", "block", "save", or "exit"
        </div>
      )}

      {/* Playlist Save Dialog - Render outside swipe overlay using portal */}
      {showSaveDialog &&
        createPortal(
          <PlaylistSaveDialog
            trackCount={queue.filter((t) => t.kept).length}
            onSave={handleSaveWithName}
            onCancel={() => {
              console.log('❌ onCancel called - closing dialog');
              setShowSaveDialog(false);
            }}
            user={user}
          />,
          document.body
        )}
    </div>
  );
}

export default SwipeOverlay;
